import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
/**
 * Simplified hook for performing an optimistic mutation with rollback support
 * using a single query key, similar to the old implementation.
 *
 * @template T - The type of the request data for the mutation function.
 * @template R - The type of the data in the query cache.
 *
 * @param {function} mutationFn - Function to perform the mutation (e.g., API call).
 * @param {QueryKey} queryKey - The query key to identify the queries to be updated.
 * @param {function} onMutate - Function to optimistically update the query cache.
 * @param {function} [options.onSuccess] - Optional callback to be called immediately after a successful optimistic update.
 *
 * @returns {object} - Returns an object containing the mutate function and the isPending state.
 */
export const useQueryMutation = (mutationFn, queryKey, onMutate, options) => {
    const queryClient = useQueryClient();
    const [isPending, setIsPending] = useState(false);
    const mutation = useMutation({
        mutationFn,
        onMutate: async (data) => {
            setIsPending(true);
            // Cancel any outgoing queries to avoid stale data
            await queryClient.cancelQueries({ queryKey });
            // Snapshot the previous cache state for rollback if needed
            const previousData = queryClient.getQueryData(queryKey);
            // Optimistically update the cache with the correct type for oldData
            queryClient.setQueryData(queryKey, (oldData) => onMutate(oldData, data));
            // Immediately call onSuccess after the optimistic update
            if (options?.onSuccess) {
                options.onSuccess();
            }
            return previousData; // Return previous state for rollback on error
        },
        onError: (_error, _variables, context) => {
            // Rollback to the previous state if the mutation fails
            if (context !== undefined) {
                queryClient.setQueryData(queryKey, context);
            }
        },
        onSettled: () => {
            setIsPending(false);
        },
    });
    return { mutate: mutation.mutate, isPending };
};
